// WeldConnectionsDialog.tsx — Weld plate / embed locations (wcvar)
// L/R checkboxes default checked (panel edges). If both unchecked, X Pos appears.
// Type, Struct ID, Fab ID are linked lookup fields with autocomplete.

import { useMemo, useRef } from 'react';
import DialogBase from './DialogBase';
import { SlotEditor } from './SlotEditor';
import { usePendingSlot } from './usePendingSlot';
import { useShallowStore } from '../store/useStore';
import { EmbedPalette } from '../utils/embedLibrary';
import type { EmbedTypeEntry } from '../utils/embedLibrary';
import type { WcData } from '../types/panelvar';

// ── Weld type lookup table ───────────────────────────────────────────────────
interface WcTypeDef { wcType: string; structId: string; fabId: string }

const BUILTIN_WC_TYPES: WcTypeDef[] = [
  { wcType: '4A', structId: 'A S6.1',   fabId: '4A' },
  { wcType: '5A', structId: 'D&F S6.1', fabId: '5A' },
  { wcType: 'M1', structId: 'H S6.2',   fabId: '' },
  { wcType: 'R1', structId: 'A S3.1',   fabId: '' },
  { wcType: 'R2', structId: 'A S3.1',   fabId: '' },
];

const SLOT_FIELDS = [
  { key: 'mark',    label: 'Mark',     type: 'text'   as const },
  { key: 'y',       label: 'Y Pos',    type: 'number' as const, unit: '"', min: 0 },
  { key: 'width',   label: 'Plate W',  type: 'number' as const, unit: '"', min: 0 },
  { key: 'height',  label: 'Plate H',  type: 'number' as const, unit: '"', min: 0 },
  { key: 'thick',   label: 'Plate T',  type: 'number' as const, unit: '"', min: 0, step: 0.0625 },
  { key: 'studs',   label: '# Studs',  type: 'number' as const, min: 0, step: 1 },
  { key: 'studDia', label: 'Stud Dia', type: 'number' as const, unit: '"', min: 0, step: 0.125 },
];

const WC_DEFAULT: WcData = {
  mark: 'WC', sideL: true, sideR: true, x: 0, y: 0,
  wcType: '', structId: '', fabId: '',
  type: 'plate', face: 'front',
  plateW: 0, plateH: 0, anchors: 0, notes: '',
  width: 8, height: 8, thick: 0.375, studs: 4, studDia: 0.75,
};

// ── Autocomplete input with datalist ─────────────────────────────────────────
function AutoInput({ value, onChange, options, placeholder }: {
  value: string; onChange: (v: string) => void; options: string[]; placeholder?: string;
}) {
  const id = useRef(`dl-${Math.random().toString(36).slice(2, 8)}`);
  return (
    <>
      <input className="slot-input" type="text" value={value}
        list={id.current} placeholder={placeholder}
        onChange={e => onChange(e.target.value)} style={{ minWidth: 80 }} />
      <datalist id={id.current}>
        {options.map(o => <option key={o} value={o} />)}
      </datalist>
    </>
  );
}

export default function WeldConnectionsDialog() {
  const { ui, project, updatePanel } = useShallowStore(s => ({
    ui: s.ui, project: s.project, updatePanel: s.updatePanel,
  }));
  const panel = project?.panels.find(p => p.id === ui.activePanelId);
  const [slots, setSlots] = usePendingSlot<WcData>('wcvar', panel?.wcvar ?? [],
    (_px, py, mark) => ({ mark, y: py }),
    { ...WC_DEFAULT, mark: 'W' });
  if (!panel) return null;

  // Merge built-in types with any already used across the project
  const wcTypes = useMemo(() => {
    const map = new Map<string, WcTypeDef>();
    BUILTIN_WC_TYPES.forEach(t => map.set(t.wcType, t));
    project?.panels.forEach(p => {
      p.wcvar.forEach(s => {
        if (s.enabled && s.data.wcType && !map.has(s.data.wcType)) {
          map.set(s.data.wcType, { wcType: s.data.wcType, structId: s.data.structId, fabId: s.data.fabId });
        }
      });
    });
    return [...map.values()];
  }, [project]);

  const typeOptions   = useMemo(() => wcTypes.map(t => t.wcType), [wcTypes]);
  const structOptions = useMemo(() => [...new Set(wcTypes.map(t => t.structId).filter(Boolean))], [wcTypes]);
  const fabOptions    = useMemo(() => [...new Set(wcTypes.map(t => t.fabId).filter(Boolean))], [wcTypes]);

  // Linked-field update: changing one field auto-fills the others from lookup
  function linkedUpdate(origIdx: number, key: string, value: unknown) {
    setSlots(prev => {
      const next = [...prev];
      const d = { ...next[origIdx].data, [key]: value } as WcData;
      if (key === 'wcType') {
        const match = wcTypes.find(t => t.wcType === value);
        if (match) { d.structId = match.structId; d.fabId = match.fabId; }
      }
      if (key === 'structId') {
        const matches = wcTypes.filter(t => t.structId === value);
        if (matches.length === 1) { d.wcType = matches[0].wcType; d.fabId = matches[0].fabId; }
      }
      next[origIdx] = { ...next[origIdx], data: d };
      return next;
    });
  }

  function addFromPalette(entry: EmbedTypeEntry) {
    const data: WcData = { ...WC_DEFAULT, ...(entry.data as Partial<WcData>) };
    setSlots(prev => {
      const idx = prev.findIndex(s => !s.enabled);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { enabled: true, data };
        return next;
      }
      if (prev.length < 15) return [...prev, { enabled: true, data }];
      return prev;
    });
  }

  // Custom fields rendered above SlotEditor's standard fields for the active slot
  function renderExtra(origIdx: number, data: WcData, updateField: (key: string, value: unknown) => void) {
    return (
      <>
        {/* L / R checkboxes */}
        <div className="slot-row" style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <label className="slot-label" style={{ minWidth: 'auto' }}>
            <input type="checkbox" checked={data.sideL}
              onChange={e => updateField('sideL', e.target.checked)} /> Left
          </label>
          <label className="slot-label" style={{ minWidth: 'auto' }}>
            <input type="checkbox" checked={data.sideR}
              onChange={e => updateField('sideR', e.target.checked)} /> Right
          </label>
        </div>

        {/* X Pos — only when both L and R are unchecked */}
        {!data.sideL && !data.sideR && (
          <div className="slot-row">
            <label className="slot-label">X Pos</label>
            <input className="slot-input" type="number" value={data.x} min={0}
              onChange={e => updateField('x', parseFloat(e.target.value) || 0)} />
            <span className="slot-unit">"</span>
          </div>
        )}

        {/* Linked Type / Struct ID / Fab ID */}
        <div className="slot-row">
          <label className="slot-label">Type</label>
          <AutoInput value={data.wcType} options={typeOptions}
            placeholder="e.g. 4A" onChange={v => linkedUpdate(origIdx, 'wcType', v)} />
        </div>
        <div className="slot-row">
          <label className="slot-label">Struct ID</label>
          <AutoInput value={data.structId} options={structOptions}
            placeholder="e.g. A S6.1" onChange={v => linkedUpdate(origIdx, 'structId', v)} />
        </div>
        <div className="slot-row">
          <label className="slot-label">Fab ID</label>
          <AutoInput value={data.fabId} options={fabOptions}
            placeholder="optional" onChange={v => linkedUpdate(origIdx, 'fabId', v)} />
        </div>
      </>
    );
  }

  return (
    <DialogBase title="Weld Connections (15 max)" onOk={() => updatePanel(panel.id, { wcvar: slots })}>
      <EmbedPalette category="weld" settings={project?.settings} onSelect={addFromPalette} />
      <SlotEditor
        slots={slots}
        fields={SLOT_FIELDS}
        maxSlots={15}
        onChange={v => setSlots(v)}
        defaultData={() => ({ ...WC_DEFAULT, mark: 'W' })}
        label="WC"
        renderExtra={renderExtra}
      />
    </DialogBase>
  );
}
